import classNames from 'classnames';
import React, { useEffect, useId, useRef, useState } from 'react';
import { useDragLayer } from 'react-dnd';
import addTestId from '../../utils/addTestId';
import { useQuill, useQuillReadOnly } from '../QuillContext';
import type { FloatValue } from '../formats/ImageBlot';
import type { SpanValue } from '../formats/Span';
import onDnDDragEnd from '../utils/onDnDDragEnd';
import CarouselCaption from './CarouselCaption';
import CarouselSlider from './CarouselSlider';
import CarouselThumbnailBar from './CarouselThumbnailBar';
import CarouselThumbnailDragPreview from './CarouselThumbnailDragPreview';
import * as styles from './ImageCarousel.module.scss';
import type { ImageCarouselHeight } from './ImageCarouselHeight';
import SingleImage from './SingleImage';
import type { CarouselItem } from './types';
import { isUploadedCarouselItem } from './types';

interface ImageCarouselProps {
  images: CarouselItem[];
  onAddImages: (files: File[]) => void;
  onReorder(images: CarouselItem[]): void;
  onRemove(image: CarouselItem): void;
  captionsAllowed?: boolean;
  onCaptionChange(image: CarouselItem, caption: string): void;
  selected: boolean;
  selectedIndex: number;
  onSelectedIndexChange: (index: number) => void;
  height: ImageCarouselHeight;
  span: SpanValue | null;
  float: FloatValue;
  hoverFileIndex: number | null;
  onHoverFileIndexChange: (index: number | null) => void;
}

const ImageCarousel = ({
  images,
  onAddImages,
  onReorder,
  onRemove,
  captionsAllowed,
  onCaptionChange,
  selected,
  selectedIndex,
  onSelectedIndexChange,
  height,
  span,
  float,
  hoverFileIndex,
  onHoverFileIndexChange,
}: ImageCarouselProps) => {
  const carouselId = useId();
  const carouselRef = useRef<HTMLDivElement>(null);
  const captionRef = useRef<HTMLDivElement>(null);

  const { quill } = useQuill();
  const readOnly = useQuillReadOnly();

  const [isHovered, setIsHovered] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const firstImage = images[0];
  const isSingleMode = images.length === 1 && !!firstImage;

  const isDraggingFromAnotherCarousel = useDragLayer((monitor) => {
    const item = monitor.getItem();
    return !!item?.carouselId && item.carouselId !== carouselId;
  });

  const isDragAndDropAllowed =
    !readOnly && !float && !isDraggingFromAnotherCarousel;

  useEffect(() => {
    if (!isDragAndDropAllowed) {
      return undefined;
    }

    const carousel = carouselRef.current;
    if (!carousel) return undefined;

    const handleDragEnter = () => {
      setIsDraggingOver(true);
    };

    const handleDrop = () => {
      setIsHovered(true);
    };

    carousel.addEventListener('dragenter', handleDragEnter);
    carousel.addEventListener('drop', handleDrop);

    const cleanup = onDnDDragEnd(carousel, () => {
      setIsDraggingOver(false);
    });

    return () => {
      cleanup();
      carousel.removeEventListener('drop', handleDrop);
      carousel.removeEventListener('dragenter', handleDragEnter);
    };
  }, [isDragAndDropAllowed]);

  const shouldShowControls =
    isHovered && images.length > 1 && !isDraggingFromAnotherCarousel;
  const shouldShowThumbnailBar =
    shouldShowControls ||
    isDraggingOver ||
    (images.some((image) => !isUploadedCarouselItem(image)) &&
      images.length > 1) ||
    hoverFileIndex != null;

  useEffect(() => {
    if (selected && !captionRef.current?.contains(document.activeElement)) {
      carouselRef.current?.focus({ preventScroll: true });
    }
  }, [selected]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const selectedIndexChange = { ArrowLeft: -1, ArrowRight: 1 }[e.key];

    if (shouldShowControls && selectedIndexChange) {
      e.stopPropagation();
      onSelectedIndexChange(
        (selectedIndex + selectedIndexChange + images.length) % images.length,
      );
    }
  };

  return (
    <>
      <div
        {...addTestId('imageCarousel')}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onKeyDown={onKeyDown}
        tabIndex={-1}
        onDragEnd={(e) => {
          if (e.dataTransfer.dropEffect === 'none') {
            onHoverFileIndexChange(null);
          }
        }}
        ref={carouselRef}
        aria-roledescription="carousel"
        className={classNames('image-container', styles.container, {
          [styles.isSingleMode]: isSingleMode,
        })}
      >
        <div className={styles.content}>
          {isSingleMode ? (
            <SingleImage image={firstImage} span={span} />
          ) : (
            <CarouselSlider
              selectedIndex={selectedIndex}
              onSelectedIndexChange={onSelectedIndexChange}
              images={images}
              height={height}
              span={span}
              showControls={shouldShowControls}
            />
          )}
          <div
            className={classNames(styles.thumbnailBar, {
              [styles.thumbnailBarHidden]: !shouldShowThumbnailBar,
            })}
          >
            <div aria-hidden className={styles.thumbnailBarBackground} />
            {quill && shouldShowThumbnailBar && (
              <CarouselThumbnailDragPreview quill={quill} />
            )}
            <CarouselThumbnailBar
              carouselId={carouselId}
              onRemove={onRemove}
              images={images}
              onAddImages={onAddImages}
              selectedIndex={selectedIndex}
              onSelectedIndexChange={onSelectedIndexChange}
              dragAndDropAllowed={isDragAndDropAllowed}
              draggingOver={isDraggingOver}
              onReorder={onReorder}
              hoverFileIndex={hoverFileIndex}
              onHoverFileIndexChange={onHoverFileIndexChange}
              dropTargetRef={carouselRef}
            />
          </div>
        </div>
      </div>
      {captionsAllowed && images[selectedIndex] && (
        <CarouselCaption
          ref={captionRef}
          image={images[selectedIndex]}
          onCaptionChange={onCaptionChange}
          active={selected || isHovered}
        />
      )}
    </>
  );
};

export default ImageCarousel;
